<script setup lang="ts">
import { ref } from 'vue';
import { t } from '../i18n';
import { FolderChoiceError, grantAccess, relocateFolder, storagePlace } from '../storage-location';
import { switchProfile } from '../profiles/session';

/**
 * Shown at start instead of the app when the profile's canvases are in a folder the app cannot open
 * yet: after a browser restart the permission must be granted again (a click), or the folder was
 * moved (point to it again). The place of a profile does not change: another folder is not accepted.
 */
const emit = defineEmits<{ done: [] }>();
const busy = ref(false);
const denied = ref(false);
const wrong = ref(false);

async function grant(): Promise<void> {
  busy.value = true;
  denied.value = false;
  if (await grantAccess()) emit('done');
  else denied.value = storagePlace.gate?.reason === 'permission';
  busy.value = false;
}
async function pick(): Promise<void> {
  busy.value = true;
  wrong.value = false;
  try {
    if (await relocateFolder()) emit('done');
  } catch (e) {
    if (e instanceof FolderChoiceError) wrong.value = true;
    else throw e;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="gate">
    <div class="float card" role="dialog" aria-labelledby="gate-title">
      <h2 id="gate-title">{{ storagePlace.gate?.reason === 'permission' ? t('storage.gate.title') : t('storage.gate.missingTitle') }}</h2>
      <p class="folder"><span class="mono">{{ storagePlace.name }}</span></p>
      <p class="muted">
        {{ storagePlace.gate?.reason === 'permission' ? t('storage.gate.text') : storagePlace.gate?.reason === 'missing' ? t('storage.gate.missing') : t('storage.gate.error') }}
      </p>
      <p v-if="denied" class="warn">{{ t('storage.gate.denied') }}</p>
      <p v-if="wrong" class="warn" role="alert">{{ t('storage.gate.notThisProfile') }}</p>
      <div class="actions">
        <button v-if="storagePlace.gate?.reason !== 'missing'" class="pill primary tall" data-act="grant-folder" :disabled="busy" @click="grant">{{ t('storage.gate.grant') }}</button>
        <button class="pill tall" :disabled="busy" data-act="relocate-folder" @click="pick">{{ t('storage.gate.pick') }}</button>
        <button class="pill tall" :disabled="busy" data-act="other-profile" @click="switchProfile">{{ t('profiles.switch') }}</button>
      </div>
      <p class="muted small">{{ t('storage.gate.note') }}</p>
    </div>
  </div>
</template>

<style scoped>
.gate {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--bg);
}
.card {
  max-width: 480px;
  width: 100%;
  padding: 24px 26px;
}
h2 {
  margin: 0 0 10px;
  font-size: 19px;
}
.folder {
  margin: 0 0 10px;
}
.mono {
  font-family: ui-monospace, Consolas, monospace;
  padding: 3px 8px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 12px;
}
.warn {
  color: var(--warn);
}
.small {
  font-size: 12px;
  margin: 0;
}
</style>
